// Servicio para manejar sites en Olimpo Web
import { API_BASE_URL } from '../config/api';

class SiteService {
  constructor() {
    this.apiBase = API_BASE_URL;
  }

  // Obtener token de autenticación
  getAuthHeaders() {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  // Verificar estado del manager
  async checkManagerStatus() {
    try {
      const headers = this.getAuthHeaders();
      
      const response = await fetch(`${this.apiBase}/api/sites/status`, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        // Si es un error 401, el token podría estar expirado
        if (response.status === 401) {
          localStorage.removeItem('auth_token');
          throw new Error('Token de autenticación expirado. Por favor, inicia sesión nuevamente.');
        }
        
        throw new Error(`Error verificando estado del manager: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error verificando estado del manager:', error);
      throw error;
    }
  }

  // Obtener todos los sites del manager
  async getMySites() {
    try {
      const response = await fetch(`${this.apiBase}/api/sites/`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Error obteniendo sites: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error en getMySites:', error);
      throw error;
    }
  }

  // Obtener un site específico
  async getSite(siteId) {
    try {
      const response = await fetch(`${this.apiBase}/api/sites/${siteId}/`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Error obteniendo site: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error en getSite:', error);
      throw error;
    }
  }

  // Crear un nuevo site
  async createSite(siteData) {
    try {
      const response = await fetch(`${this.apiBase}/api/sites`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(siteData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Error creando site: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error en createSite:', error);
      throw error;
    }
  }

  // Actualizar un site existente
  async updateSite(siteId, siteData) {
    try {
      const response = await fetch(`${this.apiBase}/api/sites/${siteId}/`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(siteData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Error actualizando site: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error en updateSite:', error);
      throw error;
    }
  }

  // Validar datos del site (actualizado para múltiples sports)
  validateSiteData(siteData) {
    const errors = {};

    // Validaciones básicas del Site
    if (!siteData.name || siteData.name.trim() === '') {
      errors.name = 'El nombre del centro deportivo es requerido';
    }

    if (!siteData.description || siteData.description.trim() === '') {
      errors.description = 'La descripción es requerida';
    }

    // Validar sports
    if (!siteData.sports || !Array.isArray(siteData.sports) || siteData.sports.length === 0) {
      errors.sports = 'Debe seleccionar al menos un deporte';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Formatear datos de Site para envío (actualizado para múltiples sports)
  formatSiteDataForSubmission(formData) {
    const baseData = {
      name: formData.name,
      description: formData.description,
      logo: formData.logo || '',
      banner: formData.banner || '',
      parent_site_id: formData.parent_site_id || ''
    };

    // Usar sports (array de IDs)
    return {
      ...baseData,
      sports: formData.sports || []
    };
  }

  // Formatear datos de Location para envío
  formatLocationDataForSubmission(formData) {
    // Validar que site_id sea válido
    const siteId = formData.site_id;
    if (!siteId || siteId.trim() === '') {
      throw new Error('Site ID es requerido y debe ser válido');
    }

    return {
      site_id: siteId,
      name: formData.name,
      address: {
        street: formData.address.street,
        city: formData.address.city,
        state: formData.address.state,
        postal_code: formData.address.postal_code,
        latitude: parseFloat(formData.address.latitude) || 0,
        longitude: parseFloat(formData.address.longitude) || 0
      },
      practice_schedules: (formData.practice_schedules || []).map(schedule => ({
        id: schedule.id,
        description: schedule.description,
        target_audience: schedule.target_audience,
        days: schedule.days || [],
        start_time: schedule.start_time || null,
        end_time: schedule.end_time || null,
        max_students: parseInt(schedule.max_students) || 0,
        is_active: Boolean(schedule.is_active),
        sport_id: schedule.sport_id ? parseInt(schedule.sport_id) : null
      })),
      monthly_cost: parseFloat(formData.monthly_cost) || 0,
      class_cost: parseFloat(formData.class_cost) || 0,
      instructor_info: {
        name: formData.instructor_info.name,
        email: formData.instructor_info.email || '',
        phone: formData.instructor_info.phone || '',
        biography: formData.instructor_info.biography || '',
        photo: formData.instructor_info.photo || '',
        credentials: formData.instructor_info.credentials || '',
        experience: parseInt(formData.instructor_info.experience) || 0
      }
    };
  }

  // ===== MÉTODOS PARA LOCATIONS =====

  // Crear una nueva location
  async createLocation(locationData) {
    try {
      const formattedData = this.formatLocationDataForSubmission(locationData);
      
      // Debug: Log the data being sent
      console.log('Creating location with data:', JSON.stringify(formattedData, null, 2));
      console.log('Endpoint:', `${this.apiBase}/api/sites/${locationData.site_id}/locations/`);
      
      const response = await fetch(`${this.apiBase}/api/sites/${locationData.site_id}/locations/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(formattedData),
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('El endpoint de ubicaciones no está disponible en el servidor. Por favor, contacta al administrador.');
        }
        
        // Debug: Log error details
        const errorText = await response.text();
        console.error('Error response:', response.status, errorText);
        
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText };
        }
        
        throw new Error(errorData.error || `Error creating location: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating location:', error);
      // Si es un error de red (Failed to fetch), proporcionar un mensaje más específico
      if (error.message === 'Failed to fetch') {
        throw new Error('No se pudo conectar con el servidor. Verifica tu conexión a internet o contacta al administrador.');
      }
      throw error;
    }
  }

  // Obtener locations de un site
  async getLocationsBySite(siteId) {
    try {
      const response = await fetch(`${this.apiBase}/api/sites/${siteId}/locations/`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error fetching locations');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching locations:', error);
      throw error;
    }
  }

  // Obtener una location específica
  async getLocation(locationId, siteId) {
    try {
      const response = await fetch(`${this.apiBase}/api/sites/${siteId}/locations/${locationId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error fetching location');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching location:', error);
      throw error;
    }
  }

  // Actualizar una location
  async updateLocation(locationId, locationData) {
    try {
      const formattedData = this.formatLocationDataForSubmission(locationData);
      const response = await fetch(`${this.apiBase}/api/sites/${locationData.site_id}/locations/${locationId}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(formattedData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error updating location');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating location:', error);
      throw error;
    }
  }

  // Eliminar una location
  async deleteLocation(locationId, siteId) {
    try {
      const response = await fetch(`${this.apiBase}/api/sites/${siteId}/locations/${locationId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error deleting location');
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting location:', error);
      throw error;
    }
  }

  // Validar datos de location
  validateLocationData(locationData) {
    const errors = {};

    if (!locationData.site_id || locationData.site_id.trim() === '') {
      errors.site_id = 'Site ID is required';
    }
    if (!locationData.name?.trim()) {
      errors.name = 'Location name is required';
    }
    if (!locationData.address?.street?.trim()) {
      errors['address.street'] = 'Street address is required';
    }
    if (!locationData.instructor_info?.name?.trim()) {
      errors['instructor_info.name'] = 'Instructor name is required';
    }

    // Validar horarios
    if (locationData.practice_schedules) {
      locationData.practice_schedules.forEach((schedule, index) => {
        if (!schedule.description?.trim()) {
          errors[`schedule_${index}_description`] = 'Schedule description is required';
        }
        if (!schedule.target_audience?.trim()) {
          errors[`schedule_${index}_audience`] = 'Target audience is required';
        }
        if (!schedule.days || schedule.days.length === 0) {
          errors[`schedule_${index}_days`] = 'At least one day must be selected';
        }
        if (schedule.max_students <= 0) {
          errors[`schedule_${index}_max_students`] = 'Max students must be greater than 0';
        }
        // NUEVO: Validar que cada horario tenga un sport_id
        if (!schedule.sport_id) {
          errors[`schedule_${index}_sport`] = 'Debe seleccionar un deporte para este horario';
        }
      });
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
}

const siteService = new SiteService();
export default siteService;
